import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { DisplayDeviceType } from '../service/displayDevice.service';

/**
 * Reusable, recolourable SVG icon for an output device.
 *
 * The glyph is drawn with `stroke="currentColor"`, so it inherits the
 * surrounding text colour, and is sized purely via the `size` input — making
 * it safe to drop into tables, hero cards, badges, etc. at any scale.
 *
 *   <app-display-device-icon [type]="'vr'" size="1.25rem" />
 *   <app-display-device-icon [type]="dev.type" [size]="48" />
 */
@Component({
    selector: 'app-display-device-icon',
    standalone: true,
    imports: [CommonModule],
    template: `
        <svg
            class="display-device-icon"
            [style.width]="sizeValue"
            [style.height]="sizeValue"
            viewBox="0 0 24 24"
            fill="none"
            [attr.aria-label]="label"
            [attr.role]="label ? 'img' : 'presentation'"
        >
            @switch (type) {
                @case ('monitor') {
                    <!-- Desktop monitor -->
                    <rect x="2.5" y="3.5" width="19" height="12" rx="1.4" stroke="currentColor" stroke-width="1.6" />
                    <path d="M9 19h6 M12 15.5V19" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
                }
                @case ('tv') {
                    <!-- Flat-screen TV on legs -->
                    <rect x="2.5" y="4.5" width="19" height="12" rx="1.4" stroke="currentColor" stroke-width="1.6" />
                    <path d="M7.5 20l2-3.5 M16.5 20l-2-3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
                }
                @case ('beamer') {
                    <!-- Projector with light cone -->
                    <rect x="2.5" y="9" width="13" height="7" rx="1.4" stroke="currentColor" stroke-width="1.6" />
                    <circle cx="9" cy="12.5" r="2" stroke="currentColor" stroke-width="1.6" />
                    <path d="M16 10.5l5-2.5v9l-5-2.5" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
                }
                @case ('vr') {
                    <!-- VR headset -->
                    <path
                        d="M4 8.5h16a1.5 1.5 0 0 1 1.5 1.5v3.5a1.5 1.5 0 0 1-1.5 1.5h-3.4a1.5 1.5 0 0 1-1.2-.6l-1-1.3a2 2 0 0 0-3.2 0l-1 1.3a1.5 1.5 0 0 1-1.2.6H4A1.5 1.5 0 0 1 2.5 13.5V10A1.5 1.5 0 0 1 4 8.5z"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linejoin="round"
                    />
                }
                @default {
                    <!-- Generic display (no/unknown type) -->
                    <rect x="2.5" y="4.5" width="19" height="12" rx="1.4" stroke="currentColor" stroke-width="1.6" />
                    <path d="M9 20h6 M12 16.5V20" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
                }
            }
        </svg>
    `,
    styles: [
        `
            .display-device-icon {
                display: inline-block;
                flex: 0 0 auto;
                vertical-align: middle;
            }
        `,
    ],
})
export class DisplayDeviceIconComponent {
    /** The output-device type; null/unknown renders a generic display glyph. */
    @Input() type: DisplayDeviceType | null = null;

    /**
     * Icon size. A bare number is treated as pixels; a string is used verbatim
     * (e.g. "1.5rem", "32px", "100%"). Defaults to a comfortable inline size.
     */
    @Input() size: string | number = '1.5rem';

    /** Optional accessible label; omit for a purely decorative icon. */
    @Input() label = '';

    get sizeValue(): string {
        return typeof this.size === 'number' ? `${this.size}px` : this.size;
    }
}
